import { formatMetricValue, parseHtml } from 'helpers/content/content';
import {
    getPlinkromaticCanonicalUrl,
    getPlinkromaticCanonicalUrlForPath,
    getPlinkromaticRelativePath,
} from 'helpers/common/common';
import { general } from 'setup/general';
import { lessons } from 'setup/lessons';

export interface PlinkromaticSeoManifestEntry {
    description: string;
    include_in_llms: boolean;
    include_in_sitemap: boolean;
    lastmod: string;
    page_type: 'landing' | 'lesson_header';
    title: string;
    url: string;
}

export interface PlinkromaticSeoManifest {
    entries: PlinkromaticSeoManifestEntry[];
    generated_at: string;
    tool: string;
}

type LessonHeaderSeo = {
    description: string;
    faqStructuredData: {
        '@context': 'https://schema.org';
        '@type': 'FAQPage';
        mainEntity: Array<{
            '@type': 'Question';
            acceptedAnswer: {
                '@type': 'Answer';
                text: string;
            };
            name: string;
        }>;
    };
    title: string;
};

type ParseLessonHtml = (html: string) => string;
type SeoEnvironment = Partial<Record<string, string | undefined>>;

const APPROVED_LESSON_HEADER_IDS = Object.entries(lessons)
    .filter(([lessonId, lessonContent]) => /^\d+$/.test(lessonId) && isIntroContent(lessonContent))
    .map(([lessonId]) => lessonId);

function isIntroContent(content: IntroContent | WizardContent): content is IntroContent {
    return typeof (content as IntroContent).heading === 'string' && typeof (content as IntroContent).button === 'string';
}

function getSeoVariables() {
    return {
        metricArticle: general.metric.article,
        metricFullName: general.metric.fullName,
        metricGoal: formatMetricValue(general.metric.unit, general.metric.goal),
        metricName: general.metricName,
    };
}

function parseSeoHtml(html: string) {
    return parseHtml(html, getSeoVariables());
}

function toPlainText(html: string) {
    return html
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/(p|ul|ol|li|div|span|b|i|u)>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function getDescriptionExcerpt(text: string, maxLength = 180) {
    if (text.length <= maxLength) {
        return text;
    }

    const candidate = text.slice(0, maxLength + 1);
    const splitAt = candidate.lastIndexOf(' ');
    const trimmed = splitAt >= maxLength - 20 ? candidate.slice(0, splitAt) : candidate.slice(0, maxLength);
    return `${trimmed.trim()}...`;
}

function parseTimestamp(rawTimestamp?: string) {
    if (!rawTimestamp) {
        return null;
    }

    if (/^\d+$/.test(rawTimestamp)) {
        const numericValue = Number(rawTimestamp);
        const timestampMs = rawTimestamp.length === 10 ? numericValue * 1000 : numericValue;
        const numericDate = new Date(timestampMs);
        return Number.isNaN(numericDate.getTime()) ? null : numericDate.toISOString();
    }

    const parsedDate = new Date(rawTimestamp);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.toISOString();
}

function buildManifestEntries(lastmod: string, env: SeoEnvironment) {
    const landingEntry: PlinkromaticSeoManifestEntry = {
        description: general.documentDescription,
        include_in_llms: true,
        include_in_sitemap: true,
        lastmod,
        page_type: 'landing',
        title: general.documentTitle,
        url: getPlinkromaticCanonicalUrl(env),
    };

    const lessonHeaderEntries = APPROVED_LESSON_HEADER_IDS.map(lessonId => {
        const lessonSeo = getPlinkromaticLessonHeaderSeo(lessonId);

        return {
            description: lessonSeo?.description || general.documentDescription,
            include_in_llms: true,
            include_in_sitemap: true,
            lastmod,
            page_type: 'lesson_header' as const,
            title: lessonSeo?.title || general.documentTitle,
            url: getPlinkromaticCanonicalUrlForPath(`/lessons/${lessonId}`, env),
        };
    });

    return [landingEntry, ...lessonHeaderEntries];
}

export function getPlinkromaticApprovedLessonHeaderIds() {
    return [...APPROVED_LESSON_HEADER_IDS];
}

export function getPlinkromaticLessonHeaderIdFromPath(path: string) {
    const relativePath = getPlinkromaticRelativePath(path);
    const match = relativePath.match(/^\/lessons\/([^/]+)$/);
    if (!match) {
        return null;
    }

    const lessonId = match[1];
    return APPROVED_LESSON_HEADER_IDS.includes(lessonId) ? lessonId : null;
}

export function isPlinkromaticApprovedLessonHeaderPath(path: string) {
    return !!getPlinkromaticLessonHeaderIdFromPath(path);
}

export function getPlinkromaticLessonHeaderContent(lessonId: string): IntroContent | null {
    const lessonContent = lessons[lessonId];
    return lessonContent && isIntroContent(lessonContent) ? lessonContent : null;
}

export function getPlinkromaticLessonHeaderSeo(
    lessonId: string,
    {
        lessonContent = getPlinkromaticLessonHeaderContent(lessonId),
        parseLessonHtml = parseSeoHtml,
    }: {
        lessonContent?: IntroContent | null;
        parseLessonHtml?: ParseLessonHtml;
    } = {}
): LessonHeaderSeo | null {
    if (!lessonContent) {
        return null;
    }

    const heading = toPlainText(parseLessonHtml(lessonContent.heading || `Level ${lessonId}`));
    const title = toPlainText(parseLessonHtml(lessonContent.title));
    const description = getDescriptionExcerpt(toPlainText(parseLessonHtml(lessonContent.text)));
    const buttonLabel = toPlainText(parseLessonHtml(lessonContent.button));

    return {
        description,
        faqStructuredData: {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: `${title}. ${description}`,
                    },
                    name: `What do you learn in ${heading} of Plinkromatic?`,
                },
                {
                    '@type': 'Question',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: `Select "${buttonLabel}" to continue from ${heading} into the next guided Plinkromatic lesson.`,
                    },
                    name: `How do you continue after ${heading}?`,
                },
            ],
        },
        title: `${heading}: ${title} | Plinkromatic | Kromatic`,
    };
}

export function getPlinkromaticSeoLastmod(env: SeoEnvironment = process.env) {
    return (
        parseTimestamp(
            env.NEXT_PUBLIC_BUILD_TIMESTAMP ||
                env.BUILD_TIMESTAMP ||
                env.VERCEL_GIT_COMMIT_DATE ||
                env.SOURCE_DATE_EPOCH
        ) || new Date().toISOString()
    );
}

export function getPlinkromaticSeoManifest(env: SeoEnvironment = process.env): PlinkromaticSeoManifest {
    const generatedAt = getPlinkromaticSeoLastmod(env);

    return {
        entries: buildManifestEntries(generatedAt, env),
        generated_at: generatedAt,
        tool: 'plinkromatic',
    };
}
